import React, { useState } from 'react';
import {
  SimpleGrid,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Stack,
  Box,
  Tag,
  Heading,
  Text as TextComponent,
} from '@chakra-ui/react';
import { FormInputs } from '../types/formInputs';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import {
  useActivitySearchQuery,
  useGetCalculateDimensions,
  useSearchProjectQuery,
} from 'api/services/Dimensions';
import { ProjectSearchData, ProjectSearchDimension } from 'api/types/types';
import { LoadingContainer } from 'components/LoadingContainer/LoadingContainer';
import { AsyncSelect } from '@landsvirkjun/react.common';
import { IconInfo } from 'components/Icons';

type AccountingInformationSectionProps = {
  errors: FieldErrors<FormInputs>;
  register: UseFormRegister<FormInputs>;
  setValue: UseFormSetValue<FormInputs>;
  control: Control<FormInputs, any>;
  watch: UseFormWatch<FormInputs>;
  emplId: string | undefined;
};

export const AccountingInformationSection = ({
  errors,
  setValue,
  control,
  watch,
  emplId,
}: AccountingInformationSectionProps): JSX.Element => {
  const { mutateAsync: searchProject } = useSearchProjectQuery();
  const { mutateAsync: searchActivity } = useActivitySearchQuery();
  const [isSearchingProject, setIsSearchingProject] = useState(false);

  const {
    refetch: getCalculatedDimensions,
    isFetching: isCalculatingDimensions,
  } = useGetCalculateDimensions(emplId, watch('workProjectId'));

  const handleSearchProject = async (ProjectId: string): Promise<void> => {
    try {
      setIsSearchingProject(true);
      const res = await searchProject({ searchText: ProjectId });
      const filterProject = res.Data?.find((el) => el.id === ProjectId);
      if (filterProject) {
        setValue('workProject', filterProject.Name);
        setValue('workProjectId', filterProject.id);
      }
    } catch (error) {
      console.error('Error searching for project:', error);
    } finally {
      setIsSearchingProject(false);
    }
  };
  const calculateDimensions = async (
    ID: string,
    ProjectId?: string
  ): Promise<void> => {
    try {
      await new Promise<void>((resolve) => {
        setValue('workProjectId', ID);
        setValue('workOrderId', '');
        setValue('workOrder', '');
        resolve();
      });
      await getCalculatedDimensions().then((res) => {
        const dimensions = res.data?.Data;
        dimensions?.map((dimension: ProjectSearchDimension) => {
          if (dimension.type === 'Location') {
            setValue('workLocation', dimension.Name || '');
            setValue('workLocationId', dimension.id || '');
          }
          if (dimension.type === 'Department') {
            setValue('workDepartment', dimension.Name || '');
            setValue('workDepartmentId', dimension.id || '');
          }
        });
      });
      if (ProjectId) await handleSearchProject(ProjectId);
    } catch (error) {
      console.error('Error fetching calculated dimensions:', error);
    }
  };

  const workProject = watch('workProject');
  const workOrder = watch('workOrder');
  const workDepartment = watch('workDepartment');
  const workLocation = watch('workLocation');
  const workProjectId = watch('workProjectId');

  return (
    <Box as="div">
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10} mb={10}>
        <Stack direction="row">
          <div>
            <Tag borderRadius="full">4</Tag>
          </div>
          <Heading as="h3" size="md">
            Bókhaldsupplýsingar
          </Heading>
        </Stack>
        <Stack>
          <Stack direction="row" alignItems="center" mb={2}>
            <IconInfo style={{ color: '#0092ce' }} />
            <FormLabel fontWeight={700}>Skráning bókhaldsupplýsinga</FormLabel>
          </Stack>
          <TextComponent color="gray.500">
            Sjálfgefið er verkið &quot;Ferðakostnaður erlendis (I103022)&quot;.
            Einungis í þeim tilfellum sem ferðast er vegna ákveðins verks eða
            verkbeiðnar ætti að velja annað.
          </TextComponent>
        </Stack>
      </SimpleGrid>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10}>
        <FormControl isInvalid={!!errors.workProject}>
          <FormLabel>Verk</FormLabel>
          <LoadingContainer isLoaded={!isSearchingProject}>
            <Controller
              control={control}
              name="workProject"
              render={({ field: { onChange } }): JSX.Element => (
                <AsyncSelect
                  placeholder="Leita eftir verki"
                  mutateAsync={searchProject}
                  onChange={(value): void => {
                    const projectOption = JSON.parse(
                      value.value
                    ) as ProjectSearchData;
                    // Name is PascalCase — backend emits "Name" to satisfy AsyncSelect's label builder
                    const { Name: name, id } = projectOption;
                    calculateDimensions(id);
                    setValue('workProject', name);
                    onChange(name);
                  }}
                  defaultValue={
                    workProject
                      ? {
                          label: `${workProject} - ${workProjectId}`,
                          value: workProject,
                        }
                      : null
                  }
                  error={!!errors.workProject}
                />
              )}
            />
          </LoadingContainer>
          <FormErrorMessage>{errors.workProject?.message}</FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={!!errors.workOrder}>
          <FormLabel>Verkbeiðni</FormLabel>
          <LoadingContainer isLoaded={!isCalculatingDimensions}>
            <Controller
              control={control}
              name="workOrder"
              render={(): JSX.Element => (
                <AsyncSelect
                  placeholder="Leita eftir verkbeiðni"
                  mutateAsync={searchActivity}
                  onChange={(value): void => {
                    const projectOption = JSON.parse(
                      value.value
                    ) as ProjectSearchData;
                    // Name is PascalCase — backend emits "Name" to satisfy AsyncSelect's label builder
                    const { id, Name: name, projectId } = projectOption;
                    calculateDimensions(id, projectId);
                    setValue('workOrder', name);
                    setValue('workOrderId', id);
                  }}
                  defaultValue={
                    workOrder
                      ? {
                          label: workOrder,
                          value: workOrder,
                        }
                      : null
                  }
                  error={!!errors.workOrder}
                />
              )}
            />
          </LoadingContainer>
          <FormErrorMessage>{errors.workOrder?.message}</FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={!!errors.workDepartment}>
          <FormLabel>Deild verks</FormLabel>
          <LoadingContainer isLoaded={!isCalculatingDimensions}>
            {workDepartment}
            {/* <Controller
              control={control}
              name="workDepartment"
              render={(): JSX.Element => (
                <AsyncSelect
                  disabled
                  placeholder="Leita eftir deild"
                  mutateAsync={searchDepartment}
                  onChange={(value): void => {
                    const projectOption = JSON.parse(
                      value.value
                    ) as ProjectSearchData;
                    const { ID } = projectOption;
                    calculateDimensions(ID);
                  }}
                  defaultValue={
                    workDepartment
                      ? {
                          label: workDepartment,
                          value: workDepartment,
                        }
                      : null
                  }
                  error={!!errors.workDepartment}
                />
              )}
            /> */}
          </LoadingContainer>
          <FormErrorMessage>{errors.workDepartment?.message}</FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={!!errors.workLocation}>
          <FormLabel>Staðsetning verks</FormLabel>
          <LoadingContainer isLoaded={!isCalculatingDimensions}>
            {workLocation}
            {/* <Controller
              control={control}
              name="workLocation"
              render={(): JSX.Element => (
                <AsyncSelect
                  disabled
                  placeholder="Leita eftir staðsetningu"
                  mutateAsync={searchLocation}
                  onChange={(value): void => {
                    const projectOption = JSON.parse(
                      value.value
                    ) as ProjectSearchData;
                    const { ID } = projectOption;
                    calculateDimensions(ID);
                  }}
                  defaultValue={
                    workLocation
                      ? {
                          label: workLocation,
                          value: workLocation,
                        }
                      : null
                  }
                  error={!!errors.workLocation}
                />
              )}
            /> */}
          </LoadingContainer>
          <FormErrorMessage>{errors.workLocation?.message}</FormErrorMessage>
        </FormControl>
      </SimpleGrid>
    </Box>
  );
};
